/**
 * ============================================================================
 * PromotionStats - KPI Cards Component
 * ============================================================================
 *
 * Displays 3 stat cards:
 * 1. Time-Based Promotions (active count)
 * 2. Recurring Discounts (courts configured)
 * 3. Total Active Discounts (combined)
 *
 * ============================================================================
 */

import React from 'react';
import { Clock, Calendar, CheckCircle } from 'lucide-react';

interface PromotionStatsProps {
  timeBasedActive: number;
  recurringCourts: number;
  totalActive: number;
}

export function PromotionStats({
  timeBasedActive,
  recurringCourts,
  totalActive,
}: PromotionStatsProps) {
  const stats = [
    {
      label: 'Time-Based Promotions',
      value: timeBasedActive.toString(),
      subtext: 'Active now',
      icon: Clock,
      colorClass: 'bg-blue-50 text-blue-600',
    },
    {
      label: 'Recurring Discounts',
      value: recurringCourts.toString(),
      subtext: 'Courts configured',
      icon: Calendar,
      colorClass: 'bg-purple-50 text-purple-600',
    },
    {
      label: 'Total Active',
      value: totalActive.toString(),
      subtext: 'All discounts',
      icon: CheckCircle,
      colorClass: 'bg-green-50 text-green-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
      {stats.map((stat, idx) => (
        <div key={idx} className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">{stat.label}</p>
              <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{stat.value}</p>
            </div>
            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${stat.colorClass} flex items-center justify-center flex-shrink-0 ml-2`}
            >
              <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
